import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { collection, onSnapshot, doc, setDoc, deleteDoc, query, where, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useGamerAuth } from './GamerAuthContext';

const dummyGamers = [
    { id: 1, name: 'MORTAL_GOD', level: 68, uid: '5592834712', kd: 4.8, role: 'IGL', avatar: 'https://i.pravatar.cc/150?img=11', lookingFor: 'SQUAD', ownerId: 'user1' },
    { id: 2, name: 'SNIPER_KING', level: 72, uid: '5348271943', kd: 5.2, role: 'Sniper', avatar: 'https://i.pravatar.cc/150?img=12', lookingFor: 'DUO', ownerId: 'user2' },
    { id: 3, name: 'RUSH_MACHINE', level: 55, uid: '5129384756', kd: 3.9, role: 'Assaulter', avatar: 'https://i.pravatar.cc/150?img=13', lookingFor: 'SQUAD', ownerId: 'user3' },
    { id: 4, name: 'NINJA_PRO', level: 61, uid: '5928374612', kd: 3.1, role: 'Support', avatar: 'https://i.pravatar.cc/150?img=14', lookingFor: 'DUO', ownerId: 'user4' },
    { id: 5, name: 'BEAST_BOY', level: 69, uid: '5847362910', kd: 4.5, role: 'Assaulter', avatar: 'https://i.pravatar.cc/150?img=15', lookingFor: 'SQUAD', ownerId: 'user5' },
    { id: 6, name: 'GHOST_RIDER', level: 59, uid: '5627384912', kd: 2.8, role: 'Sniper', avatar: 'https://i.pravatar.cc/150?img=16', lookingFor: 'DUO', ownerId: 'user6' },
    { id: 7, name: 'LONE_WOLF', level: 75, uid: '5192837465', kd: 6.1, role: 'Assaulter', avatar: 'https://i.pravatar.cc/150?img=17', lookingFor: 'SQUAD', ownerId: 'user7' },
    { id: 8, name: 'DEAD_EYE', level: 64, uid: '5738291045', kd: 4.2, role: 'Support', avatar: 'https://i.pravatar.cc/150?img=18', lookingFor: 'DUO', ownerId: 'user8' },
];

export function TeammateSkeletonCard() {
    return (
        <div className="relative uppercase font-['Monda'] overflow-hidden rounded-lg p-[2px] h-full min-h-[320px] shadow-sm dark:shadow-none group transition-all duration-300">
            {/* Spinning gradient border overlay */}
            <div className="absolute inset-[-150%] animate-[spin_4s_linear_infinite] opacity-40 group-hover:opacity-100 transition-opacity duration-500" style={{ backgroundImage: `conic-gradient(from 90deg at 50% 50%, transparent 0%, #a855f7 25%, #ec4899 50%, #a855f7 75%, transparent 100%)` }}></div>
            
            <div className="relative z-10 w-full h-full bg-white/95 dark:bg-[#1a1a24]/95 backdrop-blur-xl rounded-lg p-[15px] flex flex-col">

            <div className="relative z-10 flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-white/5 animate-pulse shrink-0"></div>
                <div className="flex-1">
                    <div className="h-6 w-32 bg-gray-200 dark:bg-white/5 animate-pulse rounded mb-2"></div>
                    <div className="h-4 w-16 bg-gray-200 dark:bg-white/5 animate-pulse rounded"></div>
                </div>
            </div>

            <div className="relative z-10 grid grid-cols-2 gap-3 mb-6">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="bg-gray-50 dark:bg-white/5 p-2 rounded-lg border border-gray-100 dark:border-white/5">
                        <div className="h-3 w-16 bg-gray-200 dark:bg-white/10 animate-pulse rounded mb-2"></div>
                        <div className="h-4 w-20 bg-gray-200 dark:bg-white/10 animate-pulse rounded"></div>
                    </div>
                ))}
            </div>

            <div className="relative z-10 mt-auto flex gap-2">
                <div className="flex-1 py-3 rounded-[50px] bg-gray-200 dark:bg-white/5 animate-pulse h-12"></div>
                <div className="flex-1 py-3 rounded-[50px] bg-gray-200 dark:bg-white/5 animate-pulse h-12"></div>
            </div>
            </div>
        </div>
    );
}

export function GamerCard({ gamer, index, isDisabled, onReport, gamerUser, friendsList = [], pendingRequests = [], isAdmin = false, onDeleteAdmin }) {
    const [isRequesting, setIsRequesting] = useState(false);
    
    // Update Modal State
    const [showUpdatePopup, setShowUpdatePopup] = useState(false);
    const [updateDetails, setUpdateDetails] = useState({ role: gamer.role || '', type: gamer.lookingFor || '', kd: gamer.kd || '' });
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const submitUpdate = async () => {
        setIsUpdating(true);
        try {
            await updateDoc(doc(db, 'findTeammates', String(gamer.id)), {
                role: updateDetails.role,
                lookingFor: updateDetails.type,
                kd: updateDetails.kd
            });
            setShowUpdatePopup(false);
        } catch (e) {
            console.error("Error updating LFT:", e);
        }
        setIsUpdating(false);
    };

    const gamerOwnerId = String(gamer.ownerId || gamer.id);
    const currentUserId = gamerUser ? String(gamerUser.uid || gamerUser.gamerCode) : null;
    
    const isMyCard = currentUserId && (gamerOwnerId === currentUserId || String(gamer.id) === currentUserId);
    
    const isAlreadyFriend = friendsList.some(req => 
        (req.senderId === currentUserId && req.receiverId === gamerOwnerId) ||
        (req.receiverId === currentUserId && req.senderId === gamerOwnerId)
    );

    const pendingRequestForThisUser = pendingRequests.find(req => req.receiverId === gamerOwnerId);
    const sentRequestType = pendingRequestForThisUser ? pendingRequestForThisUser.requestType : null;

    const handleActionClick = async (requestType = 'team') => {
        if (!gamerUser) {
            alert("Please login to perform this action!");
            return;
        }

        if (isMyCard) {
            if (isRequesting) return;
            setIsRequesting(true);
            try {
                await deleteDoc(doc(db, 'findTeammates', String(gamer.id)));
            } catch (e) {
                console.error("Error un-recruiting:", e);
            }
            setIsRequesting(false);
            return;
        }

        if (sentRequestType || isRequesting) return;
        setIsRequesting(true);
        try {
            const senderId = gamerUser.uid || gamerUser.gamerCode;
            const receiverId = gamer.ownerId || String(gamer.id);
            const reqId = `REQ_${Date.now()}_${Math.random().toString(36).substr(2,9)}`;
            
            const senderGameUid = String(gamerUser?.profileData?.primaryUidSlot === 1 ? gamerUser?.profileData?.gameUid1 : gamerUser?.profileData?.gameUid2);
            await setDoc(doc(db, 'teamRequests', reqId), {
                senderId,
                senderName: gamerUser.profileData?.name || gamerUser.displayName || 'Unknown Player',
                senderAvatar: gamerUser.photoURL || `https://ui-avatars.com/api/?name=${gamerUser.displayName || 'Player'}&background=random`,
                senderGameUid: senderGameUid && senderGameUid !== 'undefined' ? senderGameUid : senderId,
                senderRole: gamerUser.profileData?.role || 'ASSAULTER',
                senderKd: gamerUser.profileData?.kd || '3.0',
                receiverId,
                gamerData: gamer,
                status: 'pending',
                requestType: requestType,
                createdAt: Date.now()
            });
        } catch (e) {
            console.error("Error sending request:", e);
        }
        setIsRequesting(false);
    };

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            className={`relative uppercase font-['Monda'] bg-white/80 dark:bg-[#1a1a24]/80 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-lg p-[15px] shadow-sm dark:shadow-none transition-colors group flex flex-col overflow-hidden ${isDisabled ? 'opacity-50 blur-[2px] grayscale-[50%] pointer-events-none select-none' : ''}`}
        >
            {isAdmin && (
                <button 
                    onClick={(e) => { e.stopPropagation(); onDeleteAdmin && onDeleteAdmin(gamer); }}
                    className="absolute top-0 right-0 w-10 h-10 bg-red-500 hover:bg-red-600 text-white flex items-center justify-center rounded-bl-lg transition-colors z-20"
                    title="Delete LFT Profile"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
            )}

            {gamer.maxSlots && (
                <div className="absolute top-0 right-0 bg-pink-500 text-white font-black text-[10px] px-3 py-1 rounded-bl-lg z-10">
                    {gamer.filledSlots}/{gamer.maxSlots} SLOTS
                </div>
            )}
            
            {(!isMyCard && !isAdmin) && (
                <button 
                    onClick={(e) => { e.stopPropagation(); onReport && onReport(gamer); }}
                    className="absolute top-[35px] right-[15px] text-gray-400 hover:text-red-500 transition-colors z-10"
                    title="Report Player"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                </button>
            )}

            <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                    <img 
                        src={gamer.avatar || `https://i.pravatar.cc/150?u=${gamer.uid}`} 
                        alt={gamer.name} 
                        className="w-16 h-16 rounded-full border-2 border-purple-500/50 object-cover" 
                        onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${gamer.name}&background=random`; }}
                    />
                    <div className="absolute -bottom-1 -right-1 bg-green-500 w-4 h-4 rounded-full border-2 border-white dark:border-[#1a1a24]"></div>
                </div>
                <div>
                    <h3 className="text-2xl font-bold tracking-wider">{gamer.name}</h3>
                    <p className="text-xs text-pink-600 dark:text-pink-400 font-bold uppercase tracking-wider bg-pink-100 dark:bg-pink-500/10 inline-block px-2 py-0.5 rounded">
                        LVL {gamer.level}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
                <div className="bg-gray-50 dark:bg-white/5 p-2 rounded-lg border border-gray-100 dark:border-white/5">
                    <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1 uppercase">Game UID</span>
                    <span className="font-bold">{gamer.uid}</span>
                </div>
                <div className="bg-gray-50 dark:bg-white/5 p-2 rounded-lg border border-gray-100 dark:border-white/5">
                    <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1 uppercase">Looking For Role</span>
                    <span className="font-bold text-purple-600 dark:text-purple-400">{gamer.role}</span>
                </div>
                <div className="bg-gray-50 dark:bg-white/5 p-2 rounded-lg border border-gray-100 dark:border-white/5">
                    <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1 uppercase">F/D Ratio (KD)</span>
                    <span className="font-bold text-lg">{gamer.kd}</span>
                </div>
                <div className="bg-gray-50 dark:bg-white/5 p-2 rounded-lg border border-gray-100 dark:border-white/5">
                    <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1 uppercase">Looking For Team</span>
                    <span className="font-bold text-pink-600 dark:text-pink-400">{gamer.lookingFor}</span>
                </div>
            </div>

            {!isAdmin && (
                <div className="mt-auto flex gap-2">
                    {isMyCard ? (
                        <>
                            <button 
                                onClick={() => setShowUpdatePopup(true)}
                                className="flex-1 py-3 rounded-[50px] font-['Iceland'] text-lg font-bold uppercase tracking-wider transition-all duration-300 bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-gray-900 dark:text-white"
                            >
                                UPDATE
                            </button>
                            <button 
                                onClick={handleActionClick}
                                disabled={isRequesting}
                                className="flex-1 py-3 rounded-[50px] font-['Iceland'] text-lg font-bold uppercase tracking-wider transition-all duration-300 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                            >
                                {isRequesting ? 'PROCESSING...' : 'DISABLE'}
                            </button>
                        </>
                    ) : isAlreadyFriend ? (
                        <button disabled className="w-full py-3 rounded-[50px] font-['Iceland'] text-xl font-bold uppercase tracking-wider bg-green-500 text-white cursor-not-allowed flex items-center justify-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                            ALREADY FRIENDS
                        </button>
                    ) : sentRequestType === 'friend' ? (
                        <>
                            <button disabled className="flex-1 py-3 rounded-[50px] font-['Iceland'] text-lg font-bold uppercase tracking-wider bg-green-500 text-white cursor-not-allowed flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                REQUEST SENT
                            </button>
                            <button 
                                onClick={() => handleActionClick('team')}
                                disabled={isRequesting}
                                className="flex-1 py-3 rounded-[50px] font-['Iceland'] text-lg font-bold uppercase tracking-wider transition-all duration-300 bg-gradient-to-r from-pink-600 to-orange-500 hover:opacity-90 text-white disabled:opacity-50"
                            >
                                JOIN TEAM
                            </button>
                        </>
                    ) : sentRequestType === 'team' ? (
                        <>
                            <button 
                                disabled
                                className="flex-1 py-3 rounded-[50px] font-['Iceland'] text-lg font-bold uppercase tracking-wider transition-all duration-300 bg-gray-200 dark:bg-white/5 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                            >
                                ADD FRIEND
                            </button>
                            <button disabled className="flex-1 py-3 rounded-[50px] font-['Iceland'] text-lg font-bold uppercase tracking-wider bg-green-500 text-white cursor-not-allowed flex items-center justify-center gap-1">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                LFT SENT
                            </button>
                        </>
                    ) : (
                        <>
                            <button 
                                onClick={() => handleActionClick('friend')}
                                disabled={isRequesting}
                                className="flex-1 py-3 rounded-[50px] font-['Iceland'] text-lg font-bold uppercase tracking-wider transition-all duration-300 bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 text-white disabled:opacity-50"
                            >
                                ADD FRIEND
                            </button>
                            <button 
                                onClick={() => handleActionClick('team')}
                                disabled={isRequesting}
                                className="flex-1 py-3 rounded-[50px] font-['Iceland'] text-lg font-bold uppercase tracking-wider transition-all duration-300 bg-gradient-to-r from-pink-600 to-orange-500 hover:opacity-90 text-white disabled:opacity-50"
                            >
                                JOIN TEAM
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Update Popup */}
            {showUpdatePopup && typeof document !== 'undefined' && createPortal(
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm cursor-default" onClick={(e) => e.stopPropagation()}>
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white dark:bg-[#1a1a24] p-[15px] rounded-xl border border-gray-200 dark:border-white/10 shadow-2xl max-w-sm w-full font-['Monda'] uppercase"
                    >
                        <div className="border-b border-gray-200 dark:border-white/10 pb-[15px] mb-[15px] text-center">
                            <h3 className="font-['Monda'] text-xl font-black text-gray-900 dark:text-white uppercase tracking-wider">MATCHING PREFERENCES</h3>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-[15px] mb-4">
                            <div>
                                <label className="text-xs text-gray-500 dark:text-gray-400 font-bold mb-2 block uppercase tracking-wider">LOOKING FOR ROLE</label>
                                <div className="relative">
                                    <div 
                                        onClick={() => {
                                            setIsRoleDropdownOpen(!isRoleDropdownOpen);
                                            setIsTypeDropdownOpen(false);
                                        }}
                                        className="w-full font-black text-lg bg-gray-50 dark:bg-black/50 border border-gray-300 dark:border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-pink-500 transition-colors dark:text-white uppercase text-center cursor-pointer select-none"
                                    >
                                        {updateDetails.role || "SELECT"}
                                    </div>
                                    {isRoleDropdownOpen && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#1a1a24] border border-gray-200 dark:border-white/10 rounded-lg shadow-xl overflow-hidden z-[110] py-1">
                                            {["IGL", "Sniper", "Camper", "Support", "Assaulter"].map((role) => (
                                                <div
                                                    key={role}
                                                    onClick={() => {
                                                        setUpdateDetails({...updateDetails, role});
                                                        setIsRoleDropdownOpen(false);
                                                    }}
                                                    className="px-4 py-2 font-black text-center text-gray-900 dark:text-white hover:bg-pink-500 hover:text-white dark:hover:bg-pink-500 cursor-pointer uppercase transition-colors"
                                                >
                                                    {role}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs text-gray-500 dark:text-gray-400 font-bold mb-2 block uppercase tracking-wider">LOOKING FOR TEAM</label>
                                <div className="relative">
                                    <div 
                                        onClick={() => {
                                            setIsTypeDropdownOpen(!isTypeDropdownOpen);
                                            setIsRoleDropdownOpen(false);
                                        }}
                                        className="w-full font-black text-lg bg-gray-50 dark:bg-black/50 border border-gray-300 dark:border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-pink-500 transition-colors dark:text-white uppercase text-center cursor-pointer select-none"
                                    >
                                        {updateDetails.type || "SELECT"}
                                    </div>
                                    {isTypeDropdownOpen && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#1a1a24] border border-gray-200 dark:border-white/10 rounded-lg shadow-xl overflow-hidden z-[110] py-1">
                                            {["DUO", "SQUAD"].map((type) => (
                                                <div
                                                    key={type}
                                                    onClick={() => {
                                                        setUpdateDetails({...updateDetails, type});
                                                        setIsTypeDropdownOpen(false);
                                                    }}
                                                    className="px-4 py-2 font-black text-center text-gray-900 dark:text-white hover:bg-pink-500 hover:text-white dark:hover:bg-pink-500 cursor-pointer uppercase transition-colors"
                                                >
                                                    {type}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="text-xs text-gray-500 dark:text-gray-400 font-bold mb-2 block uppercase tracking-wider">MY F/D RATIO</label>
                            <input 
                                type="number" 
                                step="0.1"
                                value={updateDetails.kd}
                                onChange={(e) => setUpdateDetails({...updateDetails, kd: e.target.value})}
                                placeholder="E.G. 4.5"
                                className="w-full font-black text-lg bg-gray-50 dark:bg-black/50 border border-gray-300 dark:border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-pink-500 transition-colors dark:text-white uppercase text-center placeholder:text-left [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                        </div>

                        <div className="flex gap-[15px]">
                            <button 
                                onClick={() => setShowUpdatePopup(false)}
                                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white font-bold text-sm uppercase rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                            >
                                CANCEL
                            </button>
                            <button 
                                onClick={submitUpdate}
                                disabled={!updateDetails.role || !updateDetails.kd || !updateDetails.type || isUpdating}
                                className="flex-1 px-4 py-3 bg-pink-500 text-white font-bold text-sm uppercase rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isUpdating ? 'UPDATING...' : 'UPDATE'}
                            </button>
                        </div>
                    </motion.div>
                </div>,
                document.body
            )}
        </motion.div>
    );
}

export default function FindTeamPage() {
    const { gamerUser } = useGamerAuth();
    const [searchQuery, setSearchQuery] = useState('');
    
    // Report Modal State
    const [reportPopupOpen, setReportPopupOpen] = useState(false);
    const [reportedGamer, setReportedGamer] = useState(null);
    const [reportReason, setReportReason] = useState("");
    const [reportState, setReportState] = useState("idle");
    const [isReasonDropdownOpen, setIsReasonDropdownOpen] = useState(false);

    const handleReportClick = (gamer) => {
        setReportedGamer(gamer);
        setReportPopupOpen(true);
        setReportState("idle");
        setReportReason("");
    };

    const submitReport = () => {
        if (!reportReason) return;
        setReportState("loading");
        setTimeout(() => {
            setReportState("success");
            setTimeout(() => {
                setReportPopupOpen(false);
                setReportedGamer(null);
            }, 2000);
        }, 1500);
    };

    const [gamers, setGamers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [friendsList, setFriendsList] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);

    useEffect(() => {
        if (!gamerUser) {
            setFriendsList([]);
            return;
        }
        const userId = String(gamerUser.uid || gamerUser.gamerCode);
        const reqRef = collection(db, 'teamRequests');
        
        const q1 = query(reqRef, where('senderId', '==', userId), where('status', '==', 'accepted'));
        const q2 = query(reqRef, where('receiverId', '==', userId), where('status', '==', 'accepted'));
        
        const q3 = query(reqRef, where('senderId', '==', userId), where('status', '==', 'pending'));
        
        const u1 = onSnapshot(q1, snap => {
            setFriendsList(prev => {
                const others = prev.filter(p => p.senderId !== userId);
                const newData = snap.docs.map(d => d.data());
                return [...others, ...newData];
            });
        });
        const u2 = onSnapshot(q2, snap => {
            setFriendsList(prev => {
                const others = prev.filter(p => p.receiverId !== userId);
                const newData = snap.docs.map(d => d.data());
                return [...others, ...newData];
            });
        });
        const u3 = onSnapshot(q3, snap => {
            setPendingRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        
        return () => { u1(); u2(); u3(); };
    }, [gamerUser]);

    useEffect(() => {
        const handleSearch = (e) => setSearchQuery(e.detail);
        window.addEventListener('teamSearch', handleSearch);
        return () => window.removeEventListener('teamSearch', handleSearch);
    }, []);

    useEffect(() => {
        const collRef = collection(db, 'findTeammates');
        
        const unsubscribe = onSnapshot(collRef, async (snapshot) => {
            try {
                if (!snapshot.empty) {
                    const fetchedGamers = [];
                    const seenUids = new Set();
                    const slotCounts = {};
                    
                    snapshot.forEach(docSnap => {
                        if (docSnap.id.startsWith('TM_')) {
                            const data = docSnap.data();
                            if (data.ownerId && data.slotId) {
                                if (!slotCounts[data.ownerId]) slotCounts[data.ownerId] = { DUO: 0, SQUAD: 0 };
                                if (data.slotId.startsWith('duo')) slotCounts[data.ownerId].DUO++;
                                if (data.slotId.startsWith('squad')) slotCounts[data.ownerId].SQUAD++;
                            }
                        }
                    });

                    snapshot.forEach(docSnap => {
                        // Skip teammate slots, only show explicit LFT profiles
                        if (docSnap.id.startsWith('TM_')) return;
                        
                        const data = docSnap.data();
                        if (!seenUids.has(data.uid)) {
                            seenUids.add(data.uid);
                            const lookingFor = data.lookingFor || 'SQUAD';
                            const maxSlots = lookingFor === 'DUO' ? 2 : 4;
                            const filledSlots = slotCounts[docSnap.id]?.[lookingFor] || 0;
                            
                            fetchedGamers.push({ 
                                id: docSnap.id, 
                                ...data,
                                filledSlots,
                                maxSlots
                            });
                        }
                    });
                    setGamers(fetchedGamers);
                } else {
                    setGamers([]);
                }
            } catch (error) {
                console.error("Error fetching findTeammates data:", error);
                setGamers([]);
            }
            setLoading(false);
        }, (error) => {
            console.error("Snapshot error:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const filteredGamers = gamers.filter(gamer => 
        gamer.uid.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen pt-24 pb-12 px-4 lg:px-[15px] max-w-[120rem] mx-auto text-gray-900 dark:text-white relative">
            <div className="absolute top-[20%] left-[-10%] w-[40vw] h-[40vw] bg-purple-500/10 dark:bg-purple-600/10 rounded-full blur-[120px] pointer-events-none -z-10 transform translate-z-0"></div>
            <div className="absolute bottom-[10%] right-[-10%] w-[40vw] h-[40vw] bg-pink-500/10 dark:bg-pink-600/10 rounded-full blur-[120px] pointer-events-none -z-10 transform translate-z-0"></div>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
                </div>
            ) : gamers.length === 0 || filteredGamers.length === 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 min-[1400px]:grid-cols-4 gap-[15px] my-[15px]">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={`skel-${i}`} className="h-full">
                            <TeammateSkeletonCard />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 min-[1400px]:grid-cols-4 gap-[15px] my-[15px]">
                    {filteredGamers.map((gamer, index) => (
                        <div key={gamer.id} className="h-full">
                            <GamerCard gamer={gamer} index={index} onReport={handleReportClick} gamerUser={gamerUser} friendsList={friendsList} pendingRequests={pendingRequests} />
                        </div>
                    ))}
                    
                    {filteredGamers.length % 2 !== 0 && (
                        <div className="hidden md:block lg:hidden h-full">
                            <TeammateSkeletonCard />
                        </div>
                    )}
                    {filteredGamers.length % 3 !== 0 && Array.from({ length: 3 - (filteredGamers.length % 3) }).map((_, i) => (
                        <div key={`skel-lg-${i}`} className="hidden lg:block min-[1400px]:hidden h-full">
                            <TeammateSkeletonCard />
                        </div>
                    ))}
                    {filteredGamers.length % 4 !== 0 && Array.from({ length: 4 - (filteredGamers.length % 4) }).map((_, i) => (
                        <div key={`skel-xl-${i}`} className="hidden min-[1400px]:block h-full">
                            <TeammateSkeletonCard />
                        </div>
                    ))}
                </div>
            )}
            
            {/* Report Popup */}
            {reportPopupOpen && reportedGamer && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white dark:bg-[#1a1a24] p-[15px] rounded-xl border border-gray-200 dark:border-white/10 shadow-2xl max-w-sm w-full font-['Monda'] uppercase"
                    >
                        <div className="border-b border-gray-200 dark:border-white/10 pb-[15px] mb-[15px] text-center">
                            <h3 className="font-['Monda'] text-xl font-black text-gray-900 dark:text-white uppercase tracking-wider">REPORT GAMER</h3>
                        </div>
                        
                        {reportState === 'success' ? (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                </div>
                                <h4 className="text-lg font-black text-gray-900 dark:text-white mb-2">Report Submitted</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400 font-bold">We will review this player.</p>
                            </div>
                        ) : (
                            <>
                                <div className="mb-[15px] relative">
                                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-2 uppercase">Select Reason</label>
                                    <div 
                                        className="relative w-full cursor-pointer bg-gray-50 dark:bg-black/50 border border-gray-300 dark:border-white/10 rounded-lg px-4 py-3 text-sm text-gray-900 dark:text-white font-bold"
                                        onClick={() => setIsReasonDropdownOpen(!isReasonDropdownOpen)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span>{reportReason || "CHOOSE A REASON..."}</span>
                                            <svg className={`w-4 h-4 transition-transform ${isReasonDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                        </div>
                                    </div>
                                    {isReasonDropdownOpen && (
                                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#1a1a24] border border-gray-200 dark:border-white/10 rounded-lg shadow-xl overflow-hidden">
                                            {["Hacking / Cheating", "Abusive Language", "Toxicity / Griefing", "Scamming", "Other"].map((reason) => (
                                                <div
                                                    key={reason}
                                                    className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/5 cursor-pointer text-center"
                                                    onClick={() => {
                                                        setReportReason(reason);
                                                        setIsReasonDropdownOpen(false);
                                                    }}
                                                >
                                                    {reason}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex gap-[15px]">
                                    <button 
                                        onClick={() => setReportPopupOpen(false)}
                                        className="flex-1 py-3 rounded-lg bg-gray-200 dark:bg-white/5 text-gray-900 dark:text-white font-bold hover:bg-gray-300 dark:hover:bg-white/10 transition-colors"
                                    >
                                        CANCEL
                                    </button>
                                    <button 
                                        onClick={submitReport}
                                        disabled={!reportReason || reportState === 'loading'}
                                        className="flex-1 py-3 rounded-lg bg-red-500 hover:bg-red-600 text-white font-black transition-colors disabled:opacity-50 flex items-center justify-center"
                                    >
                                        {reportState === 'loading' ? (
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : 'SUBMIT'}
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.div>
                </div>
            )}
        </div>
    );
}
